import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useWorldCupReport } from '@/features/dashboard/hooks/useWorldCupReport';
import { Header } from '@/features/dashboard/components/Header';
import { ExportButton } from '@/features/dashboard/components/ExportButton';
import { CampaignWindow } from '@/features/dashboard/components/CampaignWindow';
import { SubTabs } from '@/features/dashboard/components/SubTabs';
import { useTabParam, type TabKey } from '@/features/dashboard/components/useTabParam';
import { LastRefreshed } from '@/features/dashboard/components/LastRefreshed';
import { LoadingPanel, ErrorPanel } from '@/features/dashboard/components/StatusPanel';
import { OverviewView } from '@/features/dashboard/views/OverviewView';
import { EngagementView } from '@/features/dashboard/views/EngagementView';
import { GamesMissionsView } from '@/features/dashboard/views/GamesMissionsView';
import { RewardsCouponsView } from '@/features/dashboard/views/RewardsCouponsView';
import { ConsentsView } from '@/features/dashboard/views/ConsentsView';
import { firstAllowedPath, type DashboardConfig } from '@/features/dashboard/config';
import { useAuth } from '@/features/auth/authStore';
import type { Brand, GamesByBrand, OverviewSegment } from '@/api/worldCup';

// When the backend doesn't ship per-brand segments (e.g. France — no sales
// data), synthesise rows from the campaign brand list. If `games_by_brand`
// is shipped, fold its per-brand plays/avg-per-user numbers in so the panel
// renders real engagement data instead of zeros. The join is by `brand_id`,
// which is the stable key across both calc functions.
function segmentsFromBrands(brands: ReadonlyArray<Brand>, gamesByBrand: ReadonlyArray<GamesByBrand> = []): OverviewSegment[] {
  const statsByBrand = new Map(gamesByBrand.map((g) => [g.brand_id, g]));
  return brands
    .filter((b) => b.pack !== 'Carrefour') // exclude umbrella entry from the per-brand list
    .map<OverviewSegment>((b) => {
      const stats = statsByBrand.get(b.id);
      return {
        brand_id: b.id,
        brand_name: b.name,
        pack: b.pack,
        weeks: b.weeks,
        game: b.game,
        // No "distinct users per brand" source on engagement-only markets —
        // leave `users` undefined; the SegmentTable shows plays + missions
        // (from games_by_brand) instead.
        plays: stats?.plays,
        missions: stats?.missions,
      };
    });
}

export function DashboardPage({ config }: { config: DashboardConfig }) {
  const [tab, setTab] = useTabParam();
  const auth = useAuth();
  const query = useWorldCupReport(config.reportId, config.apiBaseUrl);

  // `staticPayload` (when set in config) wins over the API response. Used for
  // any region whose backend pipeline isn't ready yet. `devPreviewPayload` is a
  // last resort in DEV ONLY: it lets a new dashboard be reviewed against its
  // design handoff, while a deployed build still shows the loading/error panel
  // rather than handoff numbers dressed up as live data.
  const devPreview = import.meta.env.DEV ? config.devPreviewPayload : undefined;
  const payload = config.staticPayload ?? query.data?.data ?? devPreview ?? null;
  const lastRefreshed = config.staticPayload || (!query.data && devPreview) ? null : (query.data?.meta.last_refreshed_at ?? null);

  const brandColors = useMemo(() => {
    if (!payload) return {};
    return Object.fromEntries(payload.campaign.brands.map((b) => [b.id, b.color]));
  }, [payload]);

  const brandWeeks = useMemo(() => {
    if (!payload) return {};
    return Object.fromEntries(payload.campaign.brands.map((b) => [b.id, b.weeks]));
  }, [payload]);

  const fallbackSegments = useMemo(
    () => (payload ? segmentsFromBrands(payload.campaign.brands, payload.sections.games_missions.games_by_brand) : []),
    [payload],
  );

  // Guard: signed in, but this report isn't in the token's allow-list. Bounce
  // to the first report they ARE allowed. RequireAuth already handled "no
  // token at all".
  const allowed = auth?.allowedReports ?? [];
  if (allowed.length > 0 && !allowed.includes(config.reportId)) {
    const target = firstAllowedPath(allowed);
    if (target && target !== config.path) {
      return <Navigate to={target} replace />;
    }
  }

  if (!payload) {
    if (query.isPending) return <LoadingPanel />;
    if (query.isError) return <ErrorPanel message={(query.error as Error).message} onRetry={() => query.refetch()} />;
    return null;
  }

  const { campaign, sections } = payload;
  const hide = config.hide ?? {};

  // Region-aware tab list. `consents` is a France-only sub-tab — backend
  // ships sections.consents only there. Everything else is shown for every
  // region (views handle their own emptiness).
  // `games` is dropped for campaigns with no game mechanic (config), `consents`
  // appears only where the backend ships the section.
  const availableTabs: ReadonlyArray<TabKey> = [
    'overview' as TabKey,
    'engagement' as TabKey,
    ...(hide.gamesTab ? [] : (['games'] as TabKey[])),
    'rewards' as TabKey,
    ...(sections.consents ? (['consents'] as TabKey[]) : []),
  ];
  // Active tab guard: if the URL specified ?tab=consents but this region
  // doesn't have one, render the first available tab instead.
  const effectiveTab: TabKey = availableTabs.includes(tab) ? tab : availableTabs[0];

  // Resolve the per-brand performance rows once and route them to the tab
  // declared by config (Brazil → Games). When `segmentsLocation` is unset
  // (Spain — no per-brand data), the table doesn't render on either tab.
  const segmentsLocation = config.segmentsLocation;
  const effectiveSegments = sections.overview.segments && sections.overview.segments.length > 0 ? sections.overview.segments : fallbackSegments;
  const overviewSegments = segmentsLocation === 'overview' ? effectiveSegments : undefined;
  const gamesSegments = segmentsLocation === 'games' ? sections.overview.segments : undefined;
  const gamesFallback = segmentsLocation === 'games' ? fallbackSegments : [];

  return (
    <div className="app-shell">
      <Header campaign={campaign} currentReportId={config.reportId} />
      <CampaignWindow range={campaign.date_range} />
      <SubTabs active={effectiveTab} onSelect={setTab} available={availableTabs} labels={config.tabLabels} />
      {config.hasExport && <ExportButton reportId={config.reportId} baseUrl={config.apiBaseUrl} />}

      <main>
        {effectiveTab === 'overview' && <OverviewView section={sections.overview} hide={hide} segments={overviewSegments} brandColors={brandColors} />}
        {effectiveTab === 'engagement' && <EngagementView section={sections.engagement} />}
        {effectiveTab === 'games' && (
          <GamesMissionsView
            section={sections.games_missions}
            brandColors={brandColors}
            brandWeeks={brandWeeks}
            segments={gamesSegments}
            fallbackSegments={gamesFallback}
            hide={hide}
          />
        )}
        {effectiveTab === 'rewards' && <RewardsCouponsView section={sections.rewards_coupons} brandColors={brandColors} hide={hide} />}
        {effectiveTab === 'consents' && sections.consents && (
          <ConsentsView section={sections.consents} total={sections.overview.business_kpis.consents_total} />
        )}
      </main>

      <LastRefreshed at={lastRefreshed} />
    </div>
  );
}
